import {
  BlockContextProvider,
  store as blockEditorStore,
  __experimentalUseBlockPreview as useBlockPreview,
  useBlockProps,
  useInnerBlocksProps,
} from "@wordpress/block-editor";
import { Spinner } from "@wordpress/components";
import { store as coreStore } from "@wordpress/core-data";
import { useSelect } from "@wordpress/data";
import { memo, useMemo, useState } from "@wordpress/element";
import { __ } from "@wordpress/i18n";

import SliderPreview from "../../_shared/loop-slider-preview";
import "../editor.scss";

// Default product card, mirroring themes/brandy/template-parts/query-product-layout.php
// so a fresh insert already looks like the theme's catalog card.
const TEMPLATE = [
  [
    "woocommerce/product-image",
    { showSaleBadge: false, isDescendentOfQueryLoop: true },
    [["woocommerce/product-sale-badge", { align: "left" }]],
  ],
  [
    "core/post-title",
    {
      level: 3,
      isLink: true,
      fontSize: "large",
      style: {
        spacing: { margin: { top: "0", bottom: "4px" } },
        typography: { fontStyle: "normal", fontWeight: "500" },
      },
    },
  ],
  [
    "woocommerce/product-rating",
    {
      isDescendentOfQueryLoop: true,
      style: { spacing: { margin: { top: "0", bottom: "4px" } } },
    },
  ],
  [
    "woocommerce/product-price",
    {
      isDescendentOfQueryLoop: true,
      style: { spacing: { margin: { top: "0", bottom: "8px" } } },
    },
  ],
  [
    "woocommerce/product-button",
    {
      textAlign: "left",
      isDescendentOfQueryLoop: true,
      style: { spacing: { margin: { top: "0", bottom: "0" } } },
    },
  ],
];

// Preview cap: the canvas never needs more cards than the frontend shows, and
// /wp/v2/product rejects per_page above 100.
const MAX_PREVIEW = 100;

// Mirror of get_gap_style() in Blocks/ProductTemplate.php: turn the block's own
// "Block spacing" value into a length the canvas can use, resolving the
// `var:preset|spacing|50` shorthand theme.json presets are stored in. Unset uses
// the block's own 30px default rather than the global block gap — see
// DEFAULT_GAP_STYLE in Blocks/ProductTemplate.php.
const DEFAULT_GAP = "30px"; // slider/preview only; the grid falls back in CSS (30 column / 42 row)

function resolveBlockGap(attributes) {
  const gap = attributes?.style?.spacing?.blockGap;

  if (typeof gap !== "string" || !gap) {
    return { grid: undefined, slider: DEFAULT_GAP };
  }

  const resolved = gap.startsWith("var:preset|spacing|")
    ? `var(--wp--preset--spacing--${gap.slice("var:preset|spacing|".length)})`
    : gap;

  return { grid: resolved, slider: resolved };
}

function ProductTemplateInnerBlocks() {
  const innerBlocksProps = useInnerBlocksProps(
    { className: "wp-block-brandy-product" },
    { template: TEMPLATE, __unstableDisableLayoutClassNames: true }
  );
  return <li {...innerBlocksProps} />;
}

function ProductTemplateBlockPreview({
  blocks,
  blockContextId,
  isHidden,
  setActiveBlockContextId,
}) {
  const blockPreviewProps = useBlockPreview({
    blocks,
    props: { className: "wp-block-brandy-product" },
  });

  const handleOnClick = () => setActiveBlockContextId(blockContextId);

  return (
    <li
      {...blockPreviewProps}
      tabIndex={0}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
      role="button"
      onClick={handleOnClick}
      onKeyPress={handleOnClick}
      style={{ display: isHidden ? "none" : undefined }}
    />
  );
}

const MemoizedProductTemplateBlockPreview = memo(ProductTemplateBlockPreview);

export default function Edit(props) {
  const { attributes, clientId, context } = props;
  const { displayLayout, slider, query } = context ?? {};
  const isSlider = !!slider?.enabled;
  const gap = resolveBlockGap(attributes);

  const columns = displayLayout?.columns ?? 4;
  const columnsTablet = displayLayout?.columnsTablet ?? 2;
  const columnsMobile = displayLayout?.columnsMobile ?? 1;

  // The frontend's `is-slider` class is deliberately not reused here: it exists
  // to hand the layout to Embla, which never runs in the canvas. SliderPreview
  // renders a scrollable strip instead and takes its widths from the same
  // --embla-slides-* / --embla-gap properties the frontend slider uses, so both
  // sets of custom properties are published on the wrapper in slider mode.
  // No className here: useBlockProps already contributes wp-block-brandy-product-template.
  const blockProps = useBlockProps({
    style: {
      "--bpc-columns": columns,
      "--bpc-columns-tablet": columnsTablet,
      "--bpc-columns-mobile": columnsMobile,
      // Mirrors get_image_ratio_style() in Blocks/ProductTemplate.php so the
      // editor crops the same way the frontend does.
      "--bpc-image-ratio": displayLayout?.imageRatio ?? "auto",
      ...(gap.grid ? { "--bpc-gap": gap.grid, "--bpc-row-gap": gap.grid } : {}),
      ...(isSlider
        ? {
            "--embla-slides-desktop": columns,
            "--embla-slides-tablet": columnsTablet,
            "--embla-slides-mobile": columnsMobile,
            "--embla-gap": gap.slider,
            // Dots are hidden by default via this token; the preview shows them
            // only when the block asks for them, exactly like the frontend.
            "--embla-pagination-display": slider?.showDots ? "flex" : "none",
          }
        : {}),
    },
  });

  const [activeBlockContextId, setActiveBlockContextId] = useState();

  const queryJson = useMemo(() => JSON.stringify(query ?? {}), [query]);

  const { products, blocks } = useSelect(
    (select) => {
      const { getEntityRecords } = select(coreStore);
      const { getBlocks } = select(blockEditorStore);

      return {
        // The REST route only knows core sorting and has no notion of "on sale"
        // or "featured", so the whole query attribute rides along as JSON and is
        // translated server-side by the same builder the frontend uses
        // (Blocks/ProductCollection.php -> rest_product_query). That is what keeps
        // this preview honest for price / popularity / rating ordering.
        products: getEntityRecords("postType", "product", {
          per_page: Math.min(MAX_PREVIEW, Math.max(1, query?.perPage ?? 8)),
          _fields: "id,title,link",
          context: "view",
          brandyCollectionQuery: queryJson,
        }),
        blocks: getBlocks(clientId),
      };
    },
    [clientId, queryJson]
  );

  const blockContexts = useMemo(
    () =>
      products?.map((product) => ({
        postType: "product",
        postId: product.id,
      })),
    [products]
  );

  if (!products) {
    return (
      <p {...blockProps}>
        <Spinner />
      </p>
    );
  }

  // No products match yet (empty store, or filters that exclude everything).
  // Still render the template once so the admin can build it; the child field
  // blocks fall back to their own placeholders.
  if (!products.length) {
    return (
      <>
        <p className="wp-block-brandy-product-template__notice">
          {__(
            "No products match this query yet. The card below is a preview and stays editable — it will apply to every product once there are results.",
            "brandy-blocks"
          )}
        </p>
        <ul {...blockProps}>
          <BlockContextProvider value={{ postType: "product", postId: 0 }}>
            <ProductTemplateInnerBlocks />
          </BlockContextProvider>
        </ul>
      </>
    );
  }

  const items = (blockContexts ?? []).map((blockContext) => (
    <BlockContextProvider key={blockContext.postId} value={blockContext}>
      {blockContext.postId ===
      (activeBlockContextId || blockContexts[0]?.postId) ? (
        <ProductTemplateInnerBlocks />
      ) : null}
      <MemoizedProductTemplateBlockPreview
        blocks={blocks}
        blockContextId={blockContext.postId}
        setActiveBlockContextId={setActiveBlockContextId}
        isHidden={
          blockContext.postId ===
          (activeBlockContextId || blockContexts[0]?.postId)
        }
      />
    </BlockContextProvider>
  ));

  if (isSlider) {
    return (
      <SliderPreview
        blockProps={blockProps}
        slider={slider}
        slidesPerView={columns}
        slideCount={products.length}
      >
        {items}
      </SliderPreview>
    );
  }

  return <ul {...blockProps}>{items}</ul>;
}
